import { supabase } from '@/utils/supabase';

const lastEventAt = new Map<string, number>();

export type LegacyKursiPlayEvent =
  | 'play_entry_opened'
  | 'play_cta_clicked'
  | 'daily_mission_shown'
  | 'daily_mission_started'
  | 'daily_mission_completed'
  | 'lesson_started'
  | 'lesson_completed'
  | 'lesson_completed_success'
  | 'lesson_completed_failed'
  | 'retry_clicked'
  | 'answer_submitted'
  | 'streak_warning_seen'
  | 'streak_extended'
  | 'streak_lost'
  | 'streak_saved'
  | 'league_viewed'
  | 'share_moment_shown'
  | 'share_link_copied'
  | 'creator_publish_clicked'
  | 'creator_publish_success'
  | 'creator_publish_failed'
  | 'creator_dashboard_opened'
  | 'kursi_play_error'
  | 'early_access_lead_submitted';

export type KursiLearnEvent =
  | 'learn_path_entry_opened'
  | 'learn_continue_clicked'
  | 'learn_practice_card_viewed'
  | 'learn_practice_started'
  | 'learn_practice_completed'
  | 'learn_lesson_started'
  | 'learn_lesson_completed'
  | 'learn_answer_submitted'
  | 'learn_retry_clicked'
  | 'learn_streak_warning_seen'
  | 'learn_streak_extended'
  | 'learn_streak_lost'
  | 'learn_streak_saved'
  | 'learn_league_viewed'
  | 'learn_share_opened'
  | 'learn_runtime_error'
  | 'learn_summary_viewed'
  | 'learn_profile_opened'
  | 'learn_exam_completed'
  | 'learn_review_entered'
  | 'learn_exam_entered'
  | 'learn_first_lesson_started'
  | 'learn_first_lesson_completed'
  | 'learn_first_practice_started'
  | 'learn_first_practice_completed'
  | 'learn_readiness_checkpoint';

export type KursiTelemetryEvent = LegacyKursiPlayEvent | KursiLearnEvent;

const LEGACY_TO_CANONICAL_MAP: Partial<Record<LegacyKursiPlayEvent, KursiLearnEvent>> = {
  play_entry_opened: 'learn_path_entry_opened',
  play_cta_clicked: 'learn_continue_clicked',
  daily_mission_shown: 'learn_practice_card_viewed',
  daily_mission_started: 'learn_practice_started',
  daily_mission_completed: 'learn_practice_completed',
  lesson_started: 'learn_lesson_started',
  lesson_completed: 'learn_lesson_completed',
  answer_submitted: 'learn_answer_submitted',
  retry_clicked: 'learn_retry_clicked',
  streak_warning_seen: 'learn_streak_warning_seen',
  streak_extended: 'learn_streak_extended',
  streak_lost: 'learn_streak_lost',
  streak_saved: 'learn_streak_saved',
  league_viewed: 'learn_league_viewed',
  share_moment_shown: 'learn_share_opened',
  kursi_play_error: 'learn_runtime_error',
};

export async function trackKursiPlayEvent(
  event: KursiTelemetryEvent,
  payload: Record<string, unknown> = {},
): Promise<void> {
  try {
    const legacyEvent = event as LegacyKursiPlayEvent;
    const canonicalAlias = LEGACY_TO_CANONICAL_MAP[legacyEvent];
    const eventsToWrite = canonicalAlias ? [event, canonicalAlias] : [event];

    const key = `${eventsToWrite.join('|')}:${JSON.stringify(payload).slice(0, 140)}`;
    const now = Date.now();
    const prev = lastEventAt.get(key) ?? 0;
    if (now - prev < 1200) return;
    lastEventAt.set(key, now);

    const createdAt = new Date().toISOString();
    await supabase.from('kp_telemetry_events').insert(
      eventsToWrite.map((eventName) => ({
        event: eventName,
        payload: canonicalAlias && eventName === canonicalAlias
          ? { ...payload, aliased_from: legacyEvent, schema: 'kursi_learn_v1' }
          : payload,
        created_at: createdAt,
      })),
    );
  } catch {
    // telemetry must not block UX
  }
}
